import type { Address, Aka, Person, ScanProgress } from '~/types/person/person';
import type { QRData } from '~/types/person/settings';
import type { EmailVerificationData } from '~/types/person/vault';
import { pbRequest, pbRequestRaw, pbRequestVoid } from '~/services/request';
import { ApiError } from '~/utils/errors';

/** The regions a country is divided into, as the country lookup answers them. */
export interface CountryAreas {
    areas: { code: string; name: string }[];
}

// Pure API service for person-related operations - no state management
export const personService = {
    async signPoa(personId: number, signature: string): Promise<void> {
        const nuxtApp = useNuxtApp();

        await pbRequestVoid(nuxtApp.$api.routes.people.poa.sign(personId), {
            method: 'POST',
            body: { name: signature },
            expect: 204,
            failure: 'Failed to sign POA agreement',
        });
    },

    async retractPoa(personId: number): Promise<void> {
        const nuxtApp = useNuxtApp();

        await pbRequestVoid(nuxtApp.$api.routes.people.poa.retract(personId), {
            method: 'DELETE',
            expect: 204,
            failure: 'Failed to retract POA agreement',
        });
    },

    async getMfaQrCode(): Promise<QRData> {
        const nuxtApp = useNuxtApp();

        return await pbRequest<QRData>(nuxtApp.$api.routes.me.mfa.get(), {
            failure: 'Failed to fetch QR code',
        });
    },

    async enableMfa(key: string, code: string): Promise<void> {
        const nuxtApp = useNuxtApp();

        await pbRequestVoid(nuxtApp.$api.routes.me.mfa.enable(), {
            method: 'PATCH',
            body: { key, code },
            expect: 204,
            failure: 'Failed to enable MFA',
        });
    },

    /**
     * Turns 2FA off. The API asks for the member's current authenticator code, so a session on
     * its own — a laptop left signed in, a stolen token — cannot strip the second factor.
     */
    async disableMfa(authenticator: string): Promise<void> {
        const nuxtApp = useNuxtApp();

        await pbRequestVoid(nuxtApp.$api.routes.me.mfa.disable(), {
            method: 'DELETE',
            body: { authenticator },
            expect: 204,
            failure: 'Failed to disable MFA',
        });
    },

    async getPerson(personId: number): Promise<Person> {
        const nuxtApp = useNuxtApp();

        // The route table is built by a plugin, so a call made before it runs has no URL to take.
        if (!nuxtApp.$api || !nuxtApp.$api.routes) {
            throw new ApiError('API plugin not yet initialized', 500);
        }

        return await pbRequest<Person>(nuxtApp.$api.routes.people.person(personId), {
            failure: 'Failed to fetch person data',
        });
    },

    /**
     * How much of this person's scan has finished (PB-104).
     *
     * The dashboard leaves the risk score and footprint totals on screen while the first
     * sweep runs, so they rise as it goes — this is what lets the page say they are still
     * filling in, and stop saying it when they are not.
     */
    async getScanProgress(personId: number): Promise<ScanProgress> {
        const nuxtApp = useNuxtApp();

        if (!nuxtApp.$api || !nuxtApp.$api.routes) {
            throw new ApiError('API plugin not yet initialized', 500);
        }

        return await pbRequest<ScanProgress>(nuxtApp.$api.routes.people.scanProgress(personId), {
            failure: 'Failed to read scan progress',
        });
    },

    async updatePerson(personId: number, postData: Partial<Person>): Promise<void> {
        const nuxtApp = useNuxtApp();

        await pbRequestVoid(nuxtApp.$api.routes.people.person(personId), {
            method: 'PATCH',
            body: postData,
            expect: 201,
            failure: 'Failed to update person',
        });
    },

    async addAka(personId: number, postData: Aka): Promise<void> {
        const nuxtApp = useNuxtApp();

        // 201 when the AKA was stored, 200 when the person already held that
        // name and the add was accepted as a no-op.
        await pbRequestVoid(nuxtApp.$api.routes.people.akas.create(personId), {
            method: 'POST',
            body: postData,
            expect: [200, 201],
            failure: 'Failed to add alias name',
        });
    },

    async deleteAka(personId: number, akaId: number): Promise<void> {
        const nuxtApp = useNuxtApp();

        await pbRequestVoid(nuxtApp.$api.routes.people.akas.delete(personId, akaId), {
            method: 'DELETE',
            expect: 204,
            failure: 'Failed to delete alias name',
        });
    },

    async updateAka(personId: number, akaId: number, postData: Partial<Aka>): Promise<void> {
        const nuxtApp = useNuxtApp();

        await pbRequestVoid(nuxtApp.$api.routes.people.akas.update(personId, akaId), {
            method: 'PATCH',
            body: postData,
            expect: 201,
            failure: 'Failed to update alias name',
        });
    },

    async addAddress(personId: number, postData: Address): Promise<void> {
        const nuxtApp = useNuxtApp();

        await pbRequestVoid(nuxtApp.$api.routes.people.address.create(personId), {
            method: 'POST',
            body: postData,
            expect: 201,
            failure: 'Failed to add address',
        });
    },

    async updateAddress(personId: number, addressId: number, postData: Partial<Address>): Promise<void> {
        const nuxtApp = useNuxtApp();

        await pbRequestVoid(nuxtApp.$api.routes.people.address.update(personId, addressId), {
            method: 'PATCH',
            body: postData,
            expect: 201,
            failure: 'Failed to update address',
        });
    },

    async deleteAddress(personId: number, addressId: number): Promise<void> {
        const nuxtApp = useNuxtApp();

        await pbRequestVoid(nuxtApp.$api.routes.people.address.delete(personId, addressId), {
            method: 'DELETE',
            expect: 204,
            failure: 'Failed to delete address',
        });
    },

    async deletePhone(personId: number, phoneId: number): Promise<void> {
        const nuxtApp = useNuxtApp();

        await pbRequestVoid(nuxtApp.$api.routes.people.phones.delete(personId, phoneId), {
            method: 'DELETE',
            expect: 204,
            failure: 'Failed to delete phone number',
        });
    },

    async updatePhone(personId: number, phoneId: number, postData: { primary: boolean }): Promise<void> {
        const nuxtApp = useNuxtApp();

        await pbRequestVoid(nuxtApp.$api.routes.people.phones.update(personId, phoneId), {
            method: 'PATCH',
            body: postData,
            failure: 'Failed to update phone number',
        });
    },

    async sendPhoneVerificationCode(
        personId: number,
        postData: { phone_number: string; country_code: string; method: string }
    ): Promise<void> {
        const nuxtApp = useNuxtApp();

        // The thrown error carries the response's field errors: the caller has to tell an
        // unsupported destination apart from any other rejection.
        await pbRequestVoid(nuxtApp.$api.routes.people.phones.sendVerification(personId), {
            method: 'POST',
            body: postData,
            expect: 204,
            failure: 'Failed to send phone verification code',
        });
    },

    async verifyPhoneOtp(personId: number, payload: { country_code: string; phone_number: string; code: string }): Promise<void> {
        const nuxtApp = useNuxtApp();

        await pbRequestVoid(nuxtApp.$api.routes.people.phones.create(personId), {
            method: 'POST',
            body: payload,
            expect: 201,
            failure: 'Failed to verify phone OTP',
        });
    },

    async addPhoneWithoutVerification(personId: number, payload: { country_code: string; phone_number: string }): Promise<void> {
        const nuxtApp = useNuxtApp();

        await pbRequestVoid(nuxtApp.$api.routes.people.phones.create(personId), {
            method: 'POST',
            body: {
                ...payload,
                skip_verification: true,
            },
            expect: 201,
            failure: 'Failed to add phone number',
        });
    },

    async sendEmailVerificationCode(email: string): Promise<void> {
        const nuxtApp = useNuxtApp();

        await pbRequestVoid(nuxtApp.$api.routes.people.emails.sendVerification(), {
            method: 'POST',
            body: { email },
            expect: 204,
            failure: 'Failed to send email verification code',
        });
    },

    async verifyEmailOtp(personId: number, payload: EmailVerificationData): Promise<void> {
        const nuxtApp = useNuxtApp();

        await pbRequestVoid(nuxtApp.$api.routes.people.emails.create(personId), {
            method: 'POST',
            body: payload,
            expect: [200, 201],
            failure: 'Failed to verify email OTP',
        });
    },

    async updateEmail(personId: number, emailId: number, postData: { primary: boolean }): Promise<void> {
        const nuxtApp = useNuxtApp();

        await pbRequestVoid(nuxtApp.$api.routes.people.emails.update(personId, emailId), {
            method: 'PATCH',
            body: postData,
            expect: [200, 204],
            failure: 'Failed to update email',
        });
    },

    async deleteEmail(personId: number, emailId: number): Promise<void> {
        const nuxtApp = useNuxtApp();

        await pbRequestVoid(nuxtApp.$api.routes.people.emails.delete(personId, emailId), {
            method: 'DELETE',
            expect: [200, 204],
            failure: 'Failed to delete email',
        });
    },

    async fetchStates(id: number): Promise<CountryAreas> {
        const nuxtApp = useNuxtApp();

        // The endpoint answers with the country itself, which the caller reads whole.
        return await pbRequestRaw<CountryAreas>(nuxtApp.$api.routes.countries.lookup(id), {
            method: 'GET',
            failure: 'Failed to fetch states',
        });
    },

    async fetchStatesByCode(countryCode: string): Promise<CountryAreas> {
        return await pbRequestRaw<CountryAreas>(`/countries/${countryCode}`, {
            method: 'GET',
            failure: 'Failed to fetch states',
        });
    },
};
